/**
 * Export : choisir LES pistes à inclure (multi-sélection) et le format
 * (WAV sans perte / MP3 léger). Le mix est fait dans le navigateur.
 */
import { useMemo, useState } from 'react';
import { mixdown, type EngineTrack } from '../audio/timeline';
import { encodeWav } from '../audio/wav';
import { encodeMp3 } from '../audio/mp3';
import { download } from '../audio/wav';
import { getContext } from '../audio/context';
import type { Track } from '../state/studio';
import { TRACK_COLORS } from '../state/studio';

interface Props {
  tracks: Track[];
  bpm: number;
  onClose: () => void;
  onError: (msg: string) => void;
}

export default function ExportPanel({ tracks, bpm, onClose, onError }: Props) {
  const [checked, setChecked] = useState<Set<number>>(() => {
    // par défaut : toutes les pistes audibles qui ont des clips
    const anySolo = tracks.some((t) => t.solo);
    const set = new Set<number>();
    tracks.forEach((t, i) => {
      if (t.clips.length && !t.muted && (!anySolo || t.solo)) set.add(i);
    });
    return set;
  });
  const [format, setFormat] = useState<'wav' | 'mp3'>('wav');
  const [busy, setBusy] = useState(false);

  const toggle = (i: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const engineTracks = useMemo(
    (): EngineTrack[] =>
      tracks.map((t) => ({
        // pour l'export sélectif, la sélection PRIME sur mute/solo :
        // si tu coches une piste, c'est que tu la veux dans le fichier
        volume: t.volume, muted: false, solo: false,
        clips: t.clips.map((c) => ({ buffer: c.buffer, start: c.start, offset: c.offset, duration: c.duration })),
      })),
    [tracks],
  );

  const doExport = async () => {
    if (!checked.size) { onError('Coche au moins une piste à exporter.'); return; }
    setBusy(true);
    try {
      const sr = getContext().sampleRate;
      const mix = mixdown(engineTracks, sr, checked);
      const name = `quantime-${Math.round(bpm)}bpm`;
      if (format === 'wav') {
        download(encodeWav(mix, sr), `${name}.wav`);
      } else {
        download(await encodeMp3(mix, sr), `${name}.mp3`);
      }
      onClose();
    } catch {
      onError('Export impossible (réessaie en WAV si le MP3 pose problème).');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" role="dialog" aria-label="Exporter" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong>⬇ Exporter</strong>
          <button className="btn xs ghost" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <p className="note">Coche les pistes à inclure dans le fichier :</p>
        <div className="export-tracks">
          {tracks.map((t, i) => (
            <label key={t.id} className={`export-row${t.clips.length ? '' : ' off'}`}>
              <input
                type="checkbox"
                checked={checked.has(i)}
                disabled={!t.clips.length}
                onChange={() => toggle(i)}
              />
              <span className="mx-dot" style={{ background: TRACK_COLORS[i % TRACK_COLORS.length] }} />
              <span className="export-name">{t.name}</span>
              <span className="note">{t.clips.length ? `${t.clips.length} clip${t.clips.length > 1 ? 's' : ''}` : 'vide'}</span>
            </label>
          ))}
        </div>

        <div className="export-format">
          <label title="Qualité maximale, fichier plus lourd — pour continuer à travailler le son">
            <input type="radio" name="fmt" checked={format === 'wav'} onChange={() => setFormat('wav')} />
            WAV <span className="note">(sans perte)</span>
          </label>
          <label title="Fichier léger — pour partager ou écouter">
            <input type="radio" name="fmt" checked={format === 'mp3'} onChange={() => setFormat('mp3')} />
            MP3 <span className="note">(léger, 192 kbps)</span>
          </label>
        </div>

        <button className="btn green block" onClick={doExport} disabled={busy}>
          {busy ? 'Export en cours…' : `Exporter ${checked.size} piste${checked.size > 1 ? 's' : ''} en ${format.toUpperCase()}`}
        </button>
      </div>
    </div>
  );
}
